import { useCallback, useEffect, useState } from 'react'

// Per-browser notes for the speaker, mirroring useDoneState: no login, no server,
// kept in localStorage keyed by mode id. Map of voiceover file name -> note text.
const PREFIX = 'voiceover-notes:v1:'

export type NotesState = {
  get: (id: string) => string
  set: (id: string, text: string) => void
  has: (id: string) => boolean
}

export function useNotes (storageKey: string): NotesState {
  const key = PREFIX + storageKey
  const [notes, setNotes] = useState<Record<string, string>>({})

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(key)
      setNotes(raw ? JSON.parse(raw) : {})
    } catch {
      setNotes({})
    }
  }, [key])

  const set = useCallback(
    (id: string, text: string) => {
      setNotes((prev) => {
        const next = { ...prev }
        if (text.trim().length === 0) {
          delete next[id]
        } else {
          next[id] = text
        }
        try {
          window.localStorage.setItem(key, JSON.stringify(next))
        } catch {
          /* ignore quota / disabled storage */
        }
        return next
      })
    },
    [key]
  )

  const get = useCallback((id: string) => notes[id] ?? '', [notes])
  const has = useCallback(
    (id: string) => (notes[id] ?? '').trim().length > 0,
    [notes]
  )

  return { get, set, has }
}
